// Permission Guard Component
// Phase 6 - Part 2: Frontend RBAC Support

import React from 'react';
import { hasPermission, hasAnyPermission, hasAllPermissions, hasRole, hasAnyRole } from '../../utils/permissions';

/**
 * Permission Guard Component
 * Conditionally renders children based on user permissions
 * 
 * @param {Object} props
 * @param {string|Array<string>} props.permission - Required permission(s)
 * @param {string|Array<string>} props.role - Required role(s)
 * @param {boolean} props.requireAll - If true, user must have ALL permissions (default: false - OR logic)
 * @param {React.ReactNode} props.children - Content to render if permission check passes
 * @param {React.ReactNode} props.fallback - Content to render if permission check fails (optional)
 * @param {boolean} props.hide - If true, renders nothing instead of fallback when check fails (default: false)
 */
const PermissionGuard = ({
  permission,
  role,
  requireAll = false,
  children,
  fallback = null,
  hide = false,
}) => {
  let hasAccess = false;

  // Check permissions
  if (permission) {
    if (Array.isArray(permission)) {
      hasAccess = requireAll
        ? hasAllPermissions(permission)
        : hasAnyPermission(permission);
    } else {
      hasAccess = hasPermission(permission);
    }
  }

  // Check roles (if permissions check didn't pass or no permissions specified)
  if (!hasAccess && role) {
    if (Array.isArray(role)) {
      hasAccess = hasAnyRole(role);
    } else {
      hasAccess = hasRole(role);
    }
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  if (hide) {
    return null;
  }

  return <>{fallback}</>;
};

export default PermissionGuard;

/**
 * Hook version for conditional logic in components
 * @param {string|Array<string>} permission - Required permission(s)
 * @param {boolean} requireAll - If true, user must have ALL permissions
 * @returns {boolean} True if user has required permission(s)
 */
export const usePermission = (permission, requireAll = false) => {
  if (!permission) return false;

  if (Array.isArray(permission)) {
    return requireAll
      ? hasAllPermissions(permission)
      : hasAnyPermission(permission);
  }

  return hasPermission(permission);
};

/**
 * Hook version for role checking
 * @param {string|Array<string>} role - Required role(s)
 * @returns {boolean} True if user has required role(s)
 */
export const useRole = (role) => {
  if (!role) return false;

  if (Array.isArray(role)) {
    return hasAnyRole(role);
  }

  return hasRole(role);
};
